import NiceModal, { useModal } from '@ebay/nice-modal-react';

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import BarcodeInput from 'components/field/BarcodeInput';
import Button from 'components/field/Button';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Image from 'components/global/Image';
import MinimizeButton from 'components/global/MinimizeButton';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';

import { PRODUCTS } from 'services/consts';

import AppLogger from 'utils/AppLogger';

const ImageModal = NiceModal.create(({ value, loading }) => {
	const modal = useModal();

	return (
		<Modal maxWidth="small" className="modal-imageInput">
			<Image
				url={value}
				onClick={() => modal.remove()}
				height="100%"
				style={{
					display: loading ? 'none' : 'inline-block',
					objectFit: 'contain',
					paddingTop: '3rem'
				}}
			/>
		</Modal>
	);
});

const ProductSpecsModal = NiceModal.create(({ modalName }) => {
	const modal = useModal();
	const [barcode, setBarcode] = useState('');
	const axiosPrivate = useAxiosPrivate();
	const { showError } = useAlert();
	const { closeModal } = useModalRouter();

	const formMethods = useForm({
		defaultValues: {
			name: null,
			price: null,
			size: null,
			description: null,
			imageURLs: []
		}
	});

	const { watch, reset } = formMethods;

	const getProduct = async (barcode) => {
		try {
			const response = await axiosPrivate.post(PRODUCTS.GET_PRODUCT, {
				searchVal: barcode,
				photosIncluded: true
			});
			const { data, status } = response;

			if (status === 200) {
				reset({
					name: data.name,
					price: data.price,
					size: data.size,
					description: data.description,
					imageURLs: data.imageURLs
				});
			} else {
				throw new Error('productNotFound');
			}
		} catch (error) {
			AppLogger.log(error);
			showError({
				message: 'productNotFound'
			});
		}
	};

	const handleCloseModal = () => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	};

	useEffect(() => {
		if (barcode) {
			getProduct(barcode);
		}
	}, [barcode]);

	return (
		<Modal
			fullwidth
			maxWidth="medium"
			className="modal-product-specs"
			name={modalName}
			minimizeButton={(show, close) => <MinimizeButton onShow={show} onClose={close} label="productSpecs" />}>
			<ModalTitle>
				<Typography variant="p1">productSpecs</Typography>
			</ModalTitle>
			<ModalContent>
				<Form>
					<FormItem label="productBarcodeSku" colSpan={{ md: 6 }}>
						<BarcodeInput onChange={(val) => setBarcode(val)} autoRemove={false} />
					</FormItem>
					<FormItem label="productName" colSpan={{ md: 6 }}>
						<StringInput value={watch('name')} viewMode disabled />
					</FormItem>
					<FormItem label="price" colSpan={{ md: 6 }}>
						<StringInput value={watch('price')} viewMode disabled />
					</FormItem>
					<FormItem label="productSize" colSpan={{ md: 6 }}>
						<StringInput value={watch('size')} viewMode disabled />
					</FormItem>
					<FormItem label="description">
						<StringInput value={watch('description')} viewMode disabled />
					</FormItem>
					<FormItem>
						<div style={{ display: 'flex', gap: '2rem', overflow: 'scroll' }}>
							{watch('imageURLs')?.map((url) => (
								<Image
									key={url}
									url={url}
									width="auto"
									height="200px"
									onClick={() => NiceModal.show(ImageModal, { value: url })}
								/>
							))}
						</div>
					</FormItem>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label={'close'} variant="outline" onClick={() => handleCloseModal()} />
			</ModalActions>
		</Modal>
	);
});

export default ProductSpecsModal;
